// services/chatbotAnalytics.ts
import { supabase } from '@/lib/supabaseClient';

export interface ChatbotStats {
  totalConversations: number;
  activeConversations: number;
  totalLeads: number;
  averageResponseTime: number;
  averageConversationDuration: number;
  popularQuestions: Array<{
    question: string;
    count: number;
  }>;
  conversionRate: number;
  satisfactionRate: number;
}

export const chatbotAnalytics = {
  async getStats(period: 'today' | 'week' | 'month' | 'all' = 'all'): Promise<ChatbotStats> {
    let dateFilter = new Date();
    
    switch (period) {
      case 'today':
        dateFilter.setHours(0, 0, 0, 0);
        break;
      case 'week':
        dateFilter.setDate(dateFilter.getDate() - 7);
        break;
      case 'month':
        dateFilter.setMonth(dateFilter.getMonth() - 1);
        break;
    }

    // Pobierz statystyki konwersacji
    const { data: conversations } = await supabase
      .from('chat_conversations')
      .select('*')
      .gte('started_at', period === 'all' ? '1900-01-01' : dateFilter.toISOString());

    const totalConversations = conversations?.length || 0;
    const activeConversations = conversations?.filter(c => c.status === 'active').length || 0;

    // Pobierz leady
    const { data: leads } = await supabase
      .from('leads')
      .select('*')
      .eq('source', 'chatbot')
      .gte('created_at', period === 'all' ? '1900-01-01' : dateFilter.toISOString());

    const totalLeads = leads?.length || 0;

    // Oblicz średni czas konwersacji
    const completedConversations = conversations?.filter(c => c.ended_at) || [];
    const avgDuration = completedConversations.reduce((acc, conv) => {
      const duration = new Date(conv.ended_at).getTime() - new Date(conv.started_at).getTime();
      return acc + duration;
    }, 0) / (completedConversations.length || 1);

    // Pobierz popularne pytania
    const { data: messages } = await supabase
      .from('chat_messages')
      .select('content')
      .eq('role', 'user')
      .gte('timestamp', period === 'all' ? '1900-01-01' : dateFilter.toISOString());

    const questionCounts = new Map<string, number>();
    messages?.forEach(msg => {
      const normalized = this.normalizeQuestion(msg.content);
      questionCounts.set(normalized, (questionCounts.get(normalized) || 0) + 1);
    });

    const popularQuestions = Array.from(questionCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([question, count]) => ({ question, count }));

    // Oblicz współczynnik konwersji
    const conversionRate = totalConversations > 0 
      ? (totalLeads / totalConversations) * 100 
      : 0;

    return {
      totalConversations,
      activeConversations,
      totalLeads,
      averageResponseTime: 1.2, // TODO: Implementacja rzeczywistego pomiaru
      averageConversationDuration: avgDuration / 1000 / 60, // minuty
      popularQuestions,
      conversionRate,
      satisfactionRate: 92 // TODO: Implementacja ocen użytkowników
    };
  },

  normalizeQuestion(question: string): string {
    // Podstawowa normalizacja pytań
    const normalized = question.toLowerCase()
      .replace(/[?!.,]/g, '')
      .trim();
    
    // Grupowanie podobnych pytań
    if (normalized.includes('cen') || normalized.includes('koszt')) {
      return 'Pytania o cenę';
    }
    if (normalized.includes('dostaw') || normalized.includes('wysył')) {
      return 'Pytania o dostawę';
    }
    if (normalized.includes('termin') || normalized.includes('czas')) {
      return 'Pytania o termin realizacji';
    }
    if (normalized.includes('materia') || normalized.includes('pleksi')) {
      return 'Pytania o materiały';
    }
    
    return normalized.slice(0, 50) + '...';
  },

  async logConversationEvent(conversationId: string, event: string, data?: any) {
    await supabase
      .from('chat_events')
      .insert({
        conversation_id: conversationId,
        event_type: event,
        event_data: data,
        timestamp: new Date().toISOString()
      });
  }
};